import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import licensesService from "../../services/licenses-service";
import TemplateDefault from "../../components/template-default";
import Card from "../../components/card";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import Button from "../../components/button";
import DateGenerateCellRenderer from "./components/date-generate-cell-renderer";
import {toast} from "react-toastify";
import {AlertMessage} from "../../utils/alert-message";
import LicensesModal from "./components/licenses-modal";
import {FaFileExcel} from "react-icons/fa";
import general from "../../utils/general";

const Licenses = () => {
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hardwareId, setHardwareId] = useState("");
    const [hash, setHash] = useState("");
    const [csv, setCsv] = useState(false);

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    const {code} = useParams();

    const resumeRef = useRef();
    const historyRef = useRef();

    const {data, refetch} = useQuery({
        queryFn: () => licensesService.get(code).then(({data}) => {
            setHardwareId(data?.licences[0]?.hash ?? "")
            data?.licences[0] ? setCsv(true) : setCsv(false);

            return data;
        }),
        queryKey: ['license', {code}],
        enabled: !!code
    });

    const [resumeColumnDefs] = useState([
        {field: "modality", headerName: "Modalidade", flex: 1, minWidth: 150},
        {field: "type_license", headerName: "Tipo", flex: 1, minWidth: 150},
        {field: "cameras", headerName: "Câmeras", width: 100},
        {field: "users", headerName: "Usuários", width: 100},
        {field: "ocr", headerName: "OCR", width: 100},
        {field: "contactid", headerName: "Patrimonial", width: 100},
        {field: "mobile", headerName: "Mobile", width: 100},
        {field: "expiration_date", headerName: "Data de expiração da licença", flex: 1, minWidth: 250},
    ]);

    const [historyColumnDefs] = useState([
        {field: "date_generate", headerName: "Emissão", cellRenderer: DateGenerateCellRenderer},
        {field: "modality", headerName: "Modalidade"},
        {field: "version", headerName: "Tipo"},
        {field: "lanes", headerName: "Câmeras", width: 100},
        {field: "user", headerName: "Usuários", width: 100},
        {field: "core", headerName: "OCR", width: 100},
        {field: "limitContactId", headerName: "Patrimonial", width: 110},
        {field: "limitDevices", headerName: "Mobile", width: 100},
        {field: "dateExpiration", headerName: "Expiração"},
        {field: "user_creation", headerName: "Criado por", flex: 1, minWidth: 250},
    ]);

    const defaultColDef = useMemo(() => ({resizable: false}), []);

    const generateLicence = () => {
        setIsLoading(true);

        licensesService.generator(code, hardwareId)
            .then(({data}) => {
                console.log(data)
                refetch();
                setHash(data)
                setOpen(true);
                toast.success("Licença gerada com sucesso.");
            })
            .catch((err) => {
                const {data: {message}} = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível gerar a licença");
            })
            .finally(() => setIsLoading(false));
    }

    const exportLicensing = () => {
        const newArray = data?.licences.map(data =>
            ({
                "Emissão": data.date_generate,
                "Modalidade": data.modality,
                "Tipo": data.version,
                "Câmeras": data.lanes,
                "Usuários": data.user,
                "OCR": data.core,
                "Patrimonial": data.limitContactId,
                "Mobile": data.limitDevices,
                "Expiração": data.dateExpiration,
                "Criado por": data.user_creation
            })
        );

        general.exportCsv(newArray);
    }

    return (
        <TemplateDefault title={"Gerador de licença Sentry V9 #" + code} isLoading={isLoading}>
            <Card title={data?.project.name}>
                <h3>Resumo da licença</h3>
                <div className={"ag-theme-quartz grid mt-2"}>
                    <AgGridReact
                        ref={resumeRef}
                        rowHeight={32}
                        rowData={data?.infoLicense ?? []}
                        columnDefs={resumeColumnDefs}
                        defaultColDef={defaultColDef}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        gridOptions={{localeText: LocalizationAgGrid}}
                        domLayout='autoHeight'
                    />
                </div>

                {
                    !!data?.project?.total_active_lane &&
                    <table className="border border-[#e5e7eb] shadow mt-3 w-full">
                        <thead>
                        <tr>
                            <th className="px-2 py-1 text-start border-b border-[#e5e7eb] bg-[#e6e6e6] text-[14px] font-bold">
                                Total de faixas ativas
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td className="px-2 py-1 text-start bg-[#efefef]">
                                {data?.project?.total_active_lane}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                }

                <div className={"relative mt-5"}>
                    <textarea id="hash" placeholder="" value={hardwareId} onChange={({target: {value}}) => setHardwareId(value) }
                              className="block w-full px-2.5 pb-2 pt-2 text-sm text-gray-900 bg-transparent border-[1px] rounded-[3px]  border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer h-52"/>
                    <label htmlFor="hash"
                           className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-[5px] z-10 origin-[0] bg-white px-2 peer-placeholder-shown:top-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-2.5 peer-focus:top-[5px]">
                        Hardware ID
                    </label>
                </div>

                <div className="flex justify-end mt-4">
                    <Button onClick={generateLicence} target="success">
                        Gerar Licença
                    </Button>
                </div>

                <div className="flex items-center justify-between mt-8">
                    <h3>Histórico de licenças</h3>
                    {
                        csv &&
                        <div className="flex justify-end mb-2">
                            <Button target="success" onClick={exportLicensing}>
                                <FaFileExcel/>
                            </Button>
                        </div>
                    }
                </div>

                <hr/>

                <div className={"ag-theme-quartz grid mt-4"}>
                    <AgGridReact
                        ref={historyRef}
                        rowHeight={32}
                        rowData={data?.licences ?? []}
                        columnDefs={historyColumnDefs}
                        defaultColDef={defaultColDef}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        gridOptions={{localeText: LocalizationAgGrid}}
                        domLayout='autoHeight'
                    />
                </div>
            </Card>

            <LicensesModal open={open} setOpen={setOpen} hash={hash} hardwareId={hardwareId}/>
        </TemplateDefault>
    );
};

export default Licenses;